import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Close } from "@dreamhub/designsystem/icons";
import Text from "@/components/ui/text";
import { simpleDate } from "@/utils/datetime";
import DreamerIcons from "@/components/controls/Questions/DreamerIndication/components/DreamerIcons";
import PopoverWrapper from "@/components/controls/Questions/DreamerIndication/PopoverWrapper";
import { IconButton } from "@/components/ui/icon-button";
import { iconSize } from "@/components/ui/icon-container";
import { UserData } from "@/entities/users/types";
import { Button } from "@/components/ui/button";

interface Props {
  justification: string;
  citation?: string | null;
  filledAt?: string | null;
  size?: iconSize;
  modifiedByUserData?: UserData | null;
  onRestore?: () => void;
  showChip?: boolean;
}

const ClosedLostDreamerIndication: React.FC<Props> = ({
  justification,
  citation,
  filledAt,
  size = "sm",
  modifiedByUserData,
  onRestore,
  showChip = false
}) => {
  const { t } = useTranslation();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [showPreviousVersion, setShowPreviousVersion] = useState(false);

  const isModifiedByUser = modifiedByUserData != null;

  const indicationText = isModifiedByUser
    ? t("deals.dealView.filledByDreamer.modifiedByUser", {
        userName: modifiedByUserData?.displayName
      })
    : t("deals.dealView.filledByDreamer.filledByDreamer");

  const triggerContent = (
    <DreamerIcons
      dreamerMode={!isModifiedByUser}
      isLastEditedByDreamer={!isModifiedByUser}
      lastEditedByData={isModifiedByUser ? modifiedByUserData : undefined}
      previousLastEditedBy={null}
      size={size}
    />
  );

  const dreamerVersionContent = (
    <div className="flex flex-col gap-2">
      <Text variant="bodySecondary">{justification}</Text>
      {citation && (
        <Text variant="bodyTertiary" className="italic">
          &ldquo;{citation}&rdquo;
        </Text>
      )}
      {onRestore && (
        <Button
          variant="tertiary"
          onClick={() => {
            onRestore();
            setIsPopoverOpen(false);
          }}
        >
          {t("deals.dealView.filledByDreamer.previousVersion.restoreVersion")}
        </Button>
      )}
    </div>
  );

  const popoverContent = (
    <>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <DreamerIcons
            dreamerMode={!isModifiedByUser}
            isLastEditedByDreamer={!isModifiedByUser}
            lastEditedByData={isModifiedByUser ? modifiedByUserData : undefined}
            previousLastEditedBy={null}
            size="md"
          />
          <Text variant="lgLabel">{indicationText}</Text>
        </div>
        <div className="flex items-center gap-4">
          {filledAt && (
            <Text variant="bodyTertiary">
              {simpleDate(new Date(filledAt))}
            </Text>
          )}
          <IconButton
            aria-label={t("close")}
            onClick={() => {
              setIsPopoverOpen(false);
              setShowPreviousVersion(false);
            }}
            variant="iconNoBg"
            iconColor="secondary"
            size="md"
          >
            <Close />
          </IconButton>
        </div>
      </div>

      {isModifiedByUser ? (
        showPreviousVersion ? (
          dreamerVersionContent
        ) : (
          <Button
            variant="tertiary"
            onClick={() => setShowPreviousVersion(true)}
          >
            {t("deals.dealView.filledByDreamer.previousVersionButton")}
          </Button>
        )
      ) : (
        dreamerVersionContent
      )}
    </>
  );

  return (
    <PopoverWrapper
      isPopoverOpen={isPopoverOpen}
      setIsPopoverOpen={(open) => {
        setIsPopoverOpen(open);
        if (!open) setShowPreviousVersion(false);
      }}
      triggerContent={triggerContent}
      tooltipText={indicationText}
      popoverContent={popoverContent}
      useButtonWrapper={showChip}
    />
  );
};

export { ClosedLostDreamerIndication };
